import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Box } from "@mui/material";

import { useToaster, useDownload } from "hooks";
import { error as errorVariant } from "constants";
import { UPLOAD_NAME } from "pages/BulkUpload/BulkUploadStatusHistory/constants";

import {
  RequestSummaryHeader,
  ViewToolbar,
  UploadRowsSection,
} from "./components";
import {
  fetchUploadRows,
  fetchRawAndMappedHeaderMapping,
  buildColumnsForView,
  buildRawSoListingExportUrl,
  buildFileDownloadUrl,
} from "./makeData";
import { DETAILS_VIEW, DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from "./constants";

const EMPTY_REQUEST_DEFAULTS = {
  uploadName: UPLOAD_NAME.OTHER,
  fileName: "-",
  uploadedBy: "-",
  uploadedAt: null,
};

const areArraysEqual = (a, b) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const BulkUploadStatusHistoryDetails = () => {
  const { requestId: routeRequestId } = useParams();
  const location = useLocation();

  const triggerToaster = useToaster();
  const triggerToasterRef = useRef(triggerToaster);
  triggerToasterRef.current = triggerToaster;
  const { downloadDocument } = useDownload();

  // ones fall back to EMPTY_REQUEST_DEFAULTS instead of rendering undefined
  const request = useMemo(() => {
    const stateRequest = location.state?.request ?? {};
    return {
      ...EMPTY_REQUEST_DEFAULTS,
      ...stateRequest,
      requestId: stateRequest.requestId ?? routeRequestId,
    };
  }, [location.state, routeRequestId]);

  const [view, setView] = useState(DETAILS_VIEW.MAPPED);
  const [rawColumnKeys, setRawColumnKeys] = useState([]);
  const [headerMapping, setHeaderMapping] = useState({});
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [statusFilters, setStatusFilters] = useState(() => new Set());
  const [hiddenColumns, setHiddenColumns] = useState(() => new Set());

  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const selectedStatuses = useMemo(
    () => Array.from(statusFilters),
    [statusFilters],
  );

  const fetchRows = useCallback(
    async (pageIndex, pageSize) => {
      setIsLoading(true);
      try {
        const response = await fetchUploadRows({
          uploadId: request.requestId,
          statusFilters: selectedStatuses,
          page: pageIndex,
          pageSize,
        });
        setRows(response?.data?.rows ?? []);
        setTotalCount(response?.data?.totalCount ?? 0);

        const nextColumnKeys = response?.data?.columnKeys;
        if (nextColumnKeys?.length) {
          setRawColumnKeys((prev) =>
            areArraysEqual(prev, nextColumnKeys) ? prev : nextColumnKeys,
          );
        }
        const nextStatuses = response?.data?.availableStatuses;
        if (nextStatuses?.length) {
          setAvailableStatuses((prev) =>
            areArraysEqual(prev, nextStatuses) ? prev : nextStatuses,
          );
        }
      } catch (err) {
        setRows([]);
        setTotalCount(0);
        triggerToasterRef.current(
          err?.data?.errorMessage || "Unable to fetch rows",
          errorVariant,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [request.requestId, selectedStatuses],
  );

  useEffect(() => {
    setPage(DEFAULT_PAGE);
    void fetchRows(DEFAULT_PAGE, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRows]);

  // The header-mapping dictionary is only needed for the Raw view, and is
  // fetched (and cached) lazily the first time it's selected - switching
  // back and forth afterwards is instant, no re-fetch.
  useEffect(() => {
    if (view !== DETAILS_VIEW.RAW) return;

    fetchRawAndMappedHeaderMapping(request.uploadName)
      .then(setHeaderMapping)
      .catch(() =>
        triggerToasterRef.current(
          "Unable to fetch raw column headers",
          errorVariant,
        ),
      );
  }, [view, request.uploadName]);

  const columns = useMemo(
    () => buildColumnsForView(rawColumnKeys, view, headerMapping),
    [rawColumnKeys, view, headerMapping],
  );

  const handlePageChange = useCallback(
    (_event, newPage) => {
      setPage(newPage);
      void fetchRows(newPage, rowsPerPage);
    },
    [fetchRows, rowsPerPage],
  );

  const handleRowsPerPageChange = useCallback(
    (event) => {
      const newRowsPerPage = Number.parseInt(event.target.value, 10);
      setRowsPerPage(newRowsPerPage);
      setPage(DEFAULT_PAGE);
      void fetchRows(DEFAULT_PAGE, newRowsPerPage);
    },
    [fetchRows],
  );

  const pagination = useMemo(
    () => ({ page, rowsPerPage, totalCount }),
    [page, rowsPerPage, totalCount],
  );

  const handleToggleStatusFilter = useCallback((value) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  }, []);

  const handleToggleColumn = useCallback((key) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleClear = useCallback(() => {
    setStatusFilters(new Set());
    setHiddenColumns(new Set());
  }, []);

  const handleExportRows = useCallback(() => {
    const url = buildRawSoListingExportUrl(selectedStatuses);
    downloadDocument(url, request.fileName, {
      uploadId: request.requestId,
    });
  }, [downloadDocument, request.requestId, request.fileName, selectedStatuses]);

  const handleDownloadFile = useCallback(() => {
    const url = buildFileDownloadUrl(request.requestId);
    downloadDocument(url, request.fileName);
  }, [downloadDocument, request.requestId, request.fileName]);

  return (
    <Box padding={3} px={8}>
      <RequestSummaryHeader request={request} />

      <ViewToolbar
        view={view}
        onViewChange={setView}
        availableStatuses={availableStatuses}
        statusFilters={statusFilters}
        onToggleStatusFilter={handleToggleStatusFilter}
        columns={columns}
        hiddenColumns={hiddenColumns}
        onToggleColumn={handleToggleColumn}
        onClear={handleClear}
      />

      <UploadRowsSection
        selectedStatuses={selectedStatuses}
        count={totalCount}
        rows={rows}
        columns={columns}
        hiddenColumns={hiddenColumns}
        isLoading={isLoading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onExport={handleExportRows}
        onDownloadFile={handleDownloadFile}
      />
    </Box>
  );
};

export default BulkUploadStatusHistoryDetails;
